import { setApplicationFeatures } from '../config/applicationFeatures';
import type { AxiosResponse } from 'axios';
import { apiClient, authApiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { normalizePermissions } from '../utils/permissions';
import { ApiError, normalizeApiError } from '../api/apiError';

export type AuthUser = {
  userId: number;
  userUuid: string;
  organizationId: number;
  username: string;
  email: string;
  phoneNumber?: string | null;
  employeeId?: number | null;
  employeeNumber?: string | null;
  displayName?: string | null;
  departmentId?: number | null;
  designationId?: number | null;
  locationId?: number | null;
  mustChangePassword?: boolean;
};

export type AuthEmployee = Record<string, unknown>;

export type AssignedRole = {
  roleCode: string;
  roleName: string;
  isDefault: boolean;
};

export type ActiveRole = {
  roleCode: string;
  roleName: string;
};

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  employee: AuthEmployee | null;
  organization?: Record<string, unknown> | null;
  roles: string[];
  assignedRoles: AssignedRole[];
  activeRole: ActiveRole | null;
  permissions: string[];
};

export type CurrentUser = Omit<AuthSession, 'accessToken' | 'refreshToken'>;
type Envelope<T> = { success: boolean; message: string; data: T };

function strings(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return [
    ...new Set(
      values
        .map(value =>
          typeof value === 'string'
            ? value.trim().toUpperCase()
            : '',
        )
        .filter(Boolean),
    ),
  ];
}

function parseAssignedRoles(value: unknown, fallbackCodes: string[]): AssignedRole[] {
  if (Array.isArray(value)) {
    const roles: AssignedRole[] = [];
    const seen = new Set<string>();
    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const roleCode = String(record.roleCode ?? record.role_code ?? '')
        .trim()
        .toUpperCase();
      if (!roleCode || seen.has(roleCode)) continue;
      seen.add(roleCode);
      const roleName = String(record.roleName ?? record.role_name ?? '').trim();
      roles.push({
        roleCode,
        roleName: roleName || roleCode,
        isDefault:
          record.isDefault === true ||
          record.isDefault === 1 ||
          record.is_default_role === 1,
      });
    }
    if (roles.length) return roles;
  }
  return fallbackCodes.map((roleCode, index) => ({
    roleCode,
    roleName: roleCode,
    isDefault: index === 0,
  }));
}

function parseActiveRole(
  value: unknown,
  roles: string[],
  assignedRoles: AssignedRole[],
): ActiveRole | null {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const roleCode = String(record.roleCode ?? record.role_code ?? '')
      .trim()
      .toUpperCase();
    if (roleCode) {
      const known = assignedRoles.find(role => role.roleCode === roleCode);
      const roleName = String(record.roleName ?? record.role_name ?? '').trim();
      return { roleCode, roleName: roleName || known?.roleName || roleCode };
    }
  }
  const roleCode = roles[0];
  if (!roleCode) return null;
  const known = assignedRoles.find(role => role.roleCode === roleCode);
  return { roleCode, roleName: known?.roleName || roleCode };
}

function roleState(data: Record<string, unknown>) {
  const roles = strings(data.roles);
  const assignedRoles = parseAssignedRoles(data.assignedRoles, roles);
  const activeRole = parseActiveRole(data.activeRole, roles, assignedRoles);
  const activeRoles = activeRole ? [activeRole.roleCode] : roles;
  return { roles: activeRoles, assignedRoles, activeRole };
}

function objectOrNull(value: unknown): AuthEmployee | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as AuthEmployee)
    : null;
}

function authConfigError(message: string): ApiError {
  return {
    message,
    code: 'AUTH_RESPONSE_INVALID',
  };
}

function requireToken(value: unknown, label = 'an access token'): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw authConfigError(`Authentication response did not contain ${label}.`);
  }
  return value.trim();
}

/** Native clients need the refresh token in Keychain; fall back to Set-Cookie when body omits it. */
function refreshTokenFromResponse(
  response: AxiosResponse<Envelope<Record<string, unknown>>>,
): string {
  const bodyToken = response.data?.data?.refreshToken;
  if (typeof bodyToken === 'string' && bodyToken.trim()) {
    return bodyToken.trim();
  }

  const raw = response.headers?.['set-cookie'] ?? response.headers?.['Set-Cookie'];
  const cookies = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
  for (const cookie of cookies) {
    const match = String(cookie).match(/(?:^|,\s*)refreshToken=([^;]+)/i);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1].trim());
      } catch {
        return match[1].trim();
      }
    }
  }

  throw authConfigError(
    'Authentication response did not contain a refresh token. Update the API or check cookies.',
  );
}

export async function login(input: {
  loginName: string;
  password: string;
}): Promise<AuthSession> {
  let response;
  try {
    response = await authApiClient.post<Envelope<Record<string, unknown>>>(
      API_ENDPOINTS.AUTH.LOGIN,
      input,
    );
  } catch (error) {
    throw normalizeApiError(error);
  }
  const data = response.data.data;
  return {
    accessToken: requireToken(data.accessToken),
    refreshToken: refreshTokenFromResponse(response),
    user: data.user as AuthUser,
    employee: objectOrNull(data.employee),
    ...roleState(data),
    permissions: normalizePermissions(data.permissions),
  };
}

export async function refreshSession(refreshToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
  roles: string[];
  permissions: string[];
}> {
  let response;
  try {
    response = await authApiClient.post<Envelope<Record<string, unknown>>>(
      API_ENDPOINTS.AUTH.REFRESH,
      { refreshToken },
    );
  } catch (error) {
    throw normalizeApiError(error);
  }
  const data = response.data.data;
  return {
    accessToken: requireToken(data.accessToken),
    refreshToken: refreshTokenFromResponse(response),
    roles: roleState(data).roles,
    permissions: normalizePermissions(data.permissions),
  };
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await apiClient.get<Envelope<Record<string, unknown>>>(
    API_ENDPOINTS.AUTH.ME,
  );
  const data = response.data.data;
  setApplicationFeatures(data.features);
  return {
    user: data.user as AuthUser,
    employee: objectOrNull(data.employee),
    organization: objectOrNull(data.organization),
    ...roleState(data),
    permissions: normalizePermissions(data.permissions),
  };
}

export async function switchActiveRole(roleCode: string): Promise<CurrentUser> {
  try {
    const response = await apiClient.post<Envelope<Record<string, unknown>>>(
      API_ENDPOINTS.AUTH.ACTIVE_ROLE,
      { roleCode },
    );
    const data = response.data.data;
    setApplicationFeatures(data.features);
    return {
      user: data.user as AuthUser,
      employee: objectOrNull(data.employee),
      organization: objectOrNull(data.organization),
      ...roleState(data),
      permissions: normalizePermissions(data.permissions),
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export async function logout(): Promise<void> {
  await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
}

export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  await apiClient.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, input);
}

export type PasswordResetRequestResult = {
  otpLength: number;
  otpExpiresInMinutes: number;
  resendAvailableInSeconds: number;
};

export async function requestPasswordReset(input: {
  loginName: string;
}): Promise<PasswordResetRequestResult> {
  try {
    const response = await authApiClient.post<Envelope<PasswordResetRequestResult>>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      input,
    );
    return response.data.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export async function verifyPasswordResetOtp(input: {
  loginName: string;
  otp: string;
}): Promise<{ resetToken: string; resetTokenExpiresInMinutes: number }> {
  let response;
  try {
    response = await authApiClient.post<
      Envelope<{ resetToken: string; resetTokenExpiresInMinutes: number }>
    >(API_ENDPOINTS.AUTH.FORGOT_PASSWORD_VERIFY_OTP, input);
  } catch (error) {
    throw normalizeApiError(error);
  }
  return {
    ...response.data.data,
    resetToken: requireToken(response.data.data?.resetToken, 'a reset token'),
  };
}

export async function resetForgottenPassword(input: {
  resetToken: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  try {
    await authApiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD_RESET, input);
  } catch (error) {
    throw normalizeApiError(error);
  }
}
